import * as styles from "./ContactField.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, type ReactNode, useEffect, useMemo } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxVirtualizedList,
  useComboboxVirtualizedScroll,
} from "@frontend/components/atoms/Combobox";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useField } from "@frontend/hooks/form";
import type { NumberField } from "@frontend/lib/form";
import { useLingui } from "@lingui/react/macro";

import { contactsAtom, contactsLoadingAtom, loadContactsAtom } from "../stores";
import type { ContactOption } from "../types";
import { ContactAvatar } from "./ContactAvatar";

export type ContactFieldProps = {
  field: NumberField<number | null | undefined>;
  label?: ReactNode;
  disabled?: boolean;
  error?: string | null;
  exclude?: number[];
  onContactSelected?: (contactId: number | undefined) => void;
};

export function ContactField({
  field,
  label,
  disabled,
  error,
  exclude,
  onContactSelected,
}: ContactFieldProps): JSX.Element {
  const { t } = useLingui();

  const {
    value: contactId,
    setValue: setContactId,
    error: fieldError,
  } = useField(field);

  const contacts = useAtomValue(contactsAtom);
  const loading = useAtomValue(contactsLoadingAtom);
  const loadContacts = useSetAtom(loadContactsAtom);

  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<ContactOption>();

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  const options = useMemo<ContactOption[]>(() => {
    if (undefined === exclude || 0 === exclude.length) {
      return contacts;
    }

    const held = new Set(exclude);

    return contacts.filter(
      (contact) => contact.id === contactId || !held.has(contact.id),
    );
  }, [contacts, exclude, contactId]);

  const selected = options.find((contact) => contact.id === contactId) ?? null;

  const said = error ?? fieldError;

  return (
    <Field invalid={Boolean(said)}>
      <FieldLabel>{label ?? t`Contact`}</FieldLabel>

      <FieldContent>
        <Combobox
          virtualized
          items={options}
          value={selected}
          onValueChange={(contact) => {
            const nextContactId = contact?.id ?? undefined;

            setContactId(nextContactId);
            onContactSelected?.(nextContactId);
          }}
          itemToStringLabel={(contact) => contact?.displayName ?? ""}
          isItemEqualToValue={(a, b) => a.id === b.id}
          onItemHighlighted={onItemHighlighted}
          disabled={disabled}
        >
          <ComboboxInput
            placeholder={t`Choose a contact`}
            leftSection={
              selected ? <ContactAvatar contact={selected} size="auto" /> : null
            }
            loading={loading}
          />

          <ComboboxContent
            empty={
              loading ? (
                <span className={styles.loading}>
                  <Spinner size={14} />
                  {t`Loading`}
                </span>
              ) : (
                t`No match.`
              )
            }
          >
            <ComboboxVirtualizedList<ContactOption>
              estimateSize={() => 36}
              itemKey={(contact) => String(contact.id)}
              virtualizerRef={virtualizerRef}
            >
              {(contact) => (
                <div className={styles.item}>
                  <ContactAvatar
                    contact={contact}
                    size="auto"
                    className={styles.itemAvatar}
                  />

                  <span className={styles.itemLabel}>
                    {contact.displayName}
                  </span>
                </div>
              )}
            </ComboboxVirtualizedList>
          </ComboboxContent>
        </Combobox>

        <FieldError match={Boolean(said)}>{said}</FieldError>
      </FieldContent>
    </Field>
  );
}
